import ShoppingCart from "lucide-react/icons/shopping-cart";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Highlight } from "@multiagentbook/ui";
import { BookCover, BookStatsInline } from "../../common";
import { ConvinceYourBoss } from "../../common";
import bookStatsData from "../../../data/book_stats.json";
import { CHECKOUT_URL } from "../../../data/siteLinks";

// import NavBar from "./navBar";

// In this book, you'll learn about:
// Core components of multi-agent systems and their implementation using tools like AutoGen and AutoGen Studio
// User experience design guidelines for integrating multi-agent systems into end-user applications
// Designing and building generic agents that solve problems by interacting with interfaces (e.g., carrying out actions on webpages, mobile apps, desktop apps)
// Evaluating multi-agent systems using off-the-shelf benchmarks like GAIA, GPTQ, and SWEBench, as well as creating custom benchmarks
// Optimizing performance through agent parameter tuning, small model fine-tuning, parallel processing, dedicated planning agents, and more
// Deep dives into several use cases, such as multi-agent workflows for data analysis, customer service tasks, and creativity

const HeroImage = ({ activePage = "/" }) => {
  const { t } = useTranslation("home");

  // Parse description with embedded author tag
  const renderDescription = () => {
    const description = t("hero.description");
    const parts = description.split(/<author>(.*?)<\/author>/);
    if (parts.length === 3) {
      return (
        <>
          {parts[0]}
          <Highlight>
            <a
              href="https://www.microsoft.com/en-us/research/people/victordibia/"
              target="_blank"
              rel="noreferrer"
            >
              {parts[1]}
            </a>
          </Highlight>
          {parts[2]}
        </>
      );
    }
    return description;
  };

  // Read from data/book_stats.json, which is generated from the book's chapter
  // sources. These three were typed out here and total_code_blocks had drifted
  // to 185; the book has 154, and buy.multiagentbook.com was already showing
  // the correct figure, so the two sites disagreed with each other.
  const bookStats = {
    total_chapters: bookStatsData.aggregate.total_chapters,
    total_code_blocks: bookStatsData.aggregate.total_code_blocks,
    total_figures: bookStatsData.aggregate.total_figures,
  };

  const heroDescription = (
    <div>
      {renderDescription()}{" "}
      <BookStatsInline stats={bookStats} />
      <div id="inside-the-book" className="mt-4 scroll-mt-20">
        <h3 className="text-xl font-semibold text-primary mb-3">
          {t("whatYouWillLearn.title")}
        </h3>
        <div className="text-lg ">
          <div className="flex items-start">
            <span className="text-indigo-600 mr-3 text-xl">•</span>
            <div>
              <span className="font-semibold">{t("whatYouWillLearn.multiAgentFundamentals.title")}</span> —{" "}
              {t("whatYouWillLearn.multiAgentFundamentals.description")}
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-indigo-600 mr-3 text-xl">•</span>
            <div>
              <span className="font-semibold">{t("whatYouWillLearn.buildFromScratch.title")}</span> —{" "}
              <a
                href="https://github.com/victordibia/designing-multiagent-systems/tree/main/picoagents"
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                picoagents
              </a>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-indigo-600 mr-3 text-xl">•</span>
            <div>
              <span className="font-semibold">{t("whatYouWillLearn.evaluationOptimization.title")}</span> —{" "}
              {t("whatYouWillLearn.evaluationOptimization.description")}
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-indigo-600 mr-3 text-xl">•</span>
            <div>
              <span className="font-semibold">{t("whatYouWillLearn.realWorldApplications.title")}</span> —{" "}
              {t("whatYouWillLearn.realWorldApplications.description")}
            </div>
          </div>
        </div>
        <ConvinceYourBoss className="mt-4" />
      </div>
    </div>
  );

  return (
    <div className="">
      <div className="   md:flex">
        <div className="  flex-1  lg:pr-6 pr-4">
          <main className="      ">
            <div className="text-left">
              <h1 className="text-4xl tracking-tight font-extrabold   sm:text-5xl md:text-6xl">
                <span className="text-primary  inline">
                  {t("hero.title")}{" "}
                </span>
              </h1>
              <p className="mt-3  sm:text-2xl md:text-3xl">
                {t("hero.subtitle")}
              </p>

              <div className="mt-3 text-base text-secondary sm:mt-5 sm:text-lg    md:mt-5 md:text-xl lg:mx-0">
                {heroDescription}
              </div>
            </div>
          </main>
        </div>
        <div className="md:w-3/12 mt-4 md:mt-0  ">
          {" "}
          {/* TODO: Add "Notify Me" button here for email notifications */}
          <div className="mb-4 space-y-3">
            <div className="rounded-md shadow">
              <a
                href={CHECKOUT_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white hover:text-white bg-indigo-600 hover:bg-indigo-700 md:py-3 md:text-lg md:px-6 sm:text-base cursor-pointer transition-colors duration-200"
              >
                <ShoppingCart className="w-6 mr-2 inline-block" /> {t("pricing.getDigitalEdition", { ns: "home" })}
              </a>
            </div>
            <div className="rounded-md shadow">
              <a
                href="https://www.amazon.com/dp/B0G2BCQQJY"
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center px-4 py-3 border border-indigo-600 text-base font-medium rounded-md text-indigo-600 hover:text-indigo-700 bg-white hover:bg-gray-50 md:py-3 md:text-lg md:px-6 sm:text-base cursor-pointer transition-colors duration-200"
              >
                <ShoppingCart className="w-6 mr-2 inline-block" /> {t("pricing.buyOnAmazon", { ns: "home" })}
              </a>
            </div>
          </div>
          <div className="z-30 w-full">
            <BookCover width="w-full" showTOCButton={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroImage;
